// Stripe post-payment submission engine (continueSubmissionAfterPayment
// and its renewal / new-member branch handlers + shared leaves).
//
// Extracted verbatim from DealerForm.vue; merged back via `mixins`.
import {
  insertStripeCustomerPaymentMethod,
  manageSubscriptionSchedule,
} from "@/services/api/stripe";
import { postMembership, patchMembership } from "@/services/api/membership";
import { postAccount, patchAccount } from "@/services/api/account";
import {
  postContact,
  patchContact,
  postContactRole,
} from "@/services/api/contact";
import { postBoat, patchBoat } from "@/services/api/boat";
import { postOpportunity } from "@/services/api/opportunity";
import { postPayment } from "@/services/api/payment";
import { postEmail } from "@/services/api/email";
import { cancelArbs } from "@/services/api/utility";
import {
  STRIPE_PROCESSOR_CONNECTION_SFID,
  RECORDTYPE_PERSON_CONTACT,
} from "@/config/salesforceIds";
import { MEMBERSHIP_DISPLAY_ONLY_FIELDS } from "../constants";

export default {
  methods: {
    // Attach the used kit's SFID to a payment payload so the API can mark the
    // kit Used and stamp member/AOR. No-op when no activation kit was applied.
    attachActivationCode(data) {
      if (this.activation_codes?.sfid) {
        data.activation_code_sfid = this.activation_codes.sfid;
      }
      return data;
    },
    async continueSubmissionAfterPayment(opp_guid) {
      // This method handles the post-payment flow for Stripe payments
      // It replicates the Salesforce insertion logic from the Authorize.net flow
      // Handles subscription schedules for automatic renewals

      console.log(
        "continueSubmissionAfterPayment called with opp_guid:",
        opp_guid
      );
      console.log("transId (payment intent ID):", this.transId);
      console.log("isRenew:", this.isRenew);

      // Disable submit to prevent double-submission
      this.disableSubmit = true;

      // Parse objects for Salesforce insertion
      var account_parsed_obj = JSON.parse(JSON.stringify(this.account));
      var account_keynames = Object.keys(account_parsed_obj);

      var contact_parsed_obj = JSON.parse(JSON.stringify(this.contacts));
      var contact_keynames = Object.keys(contact_parsed_obj);

      var boat_parsed_obj = JSON.parse(JSON.stringify(this.boats));
      var boat_keynames = Object.keys(boat_parsed_obj);

      var membership_parsed_obj = JSON.parse(JSON.stringify(this.memberships));
      // Strip display-only fields so they are not sent to the membership write.
      MEMBERSHIP_DISPLAY_ONLY_FIELDS.forEach(
        (field) => delete membership_parsed_obj[field]
      );
      var membership_keynames = Object.keys(membership_parsed_obj);

      const parsed = {
        account_parsed_obj,
        account_keynames,
        contact_parsed_obj,
        contact_keynames,
        boat_parsed_obj,
        boat_keynames,
        membership_parsed_obj,
        membership_keynames,
      };

      // Branch: Renewal vs New Member
      if (this.isRenew) {
        return this.handleRenewalSubmission(opp_guid, parsed);
      }
      return this.handleNewMemberSubmission(opp_guid, parsed);
    },
    async handleRenewalSubmission(opp_guid, {
      account_parsed_obj,
      account_keynames,
      contact_parsed_obj,
      contact_keynames,
      boat_parsed_obj,
      boat_keynames,
      membership_parsed_obj,
      membership_keynames,
    }) {
      // ===== RENEWAL FLOW =====
      let data = {};

      // Cancel existing Auth.net ARBs if member had auto-renew enabled
      // This is critical when switching from Auth.net to Stripe payment
      if (this.memberships.auto_renew__c) {
        console.log("Cancelling Auth.net ARBs for renewal (Stripe flow)");

        console.log(
          "Attempting to cancel ARB for membership:",
          this.membership_sfid
        );

        try {
          const cancelResponse = await cancelArbs(this.membership_sfid);
          console.log(
            "Auth.net ARB cancellation successful:",
            cancelResponse.data
          );
        } catch (error) {
          console.error(
            "Error cancelling Auth.net ARB, cannot continue",
            error
          );
          this.$bvToast.toast(
            "Error cancelling existing auto-renewal: " + error,
            {
              variant: "danger",
              title: "Error cancelling ARB",
              autoHideDelay: 5000,
            }
          );
          this.disableSubmit = false;
          return;
        }
      }

      this.$bvToast.toast("Starting the renewal process.", {
        variant: "info",
        title: "Starting renewal.",
        autoHideDelay: 5000,
      });

      data["heroku_external_id__c"] = opp_guid;
      data["name"] = "Pending Invoice Number";
      data["closedate"] = new Date().toISOString();
      data["stagename"] = "Invoice Open";
      data["accountid"] = this.account_sfid;
      data["membership__c"] = this.membership_sfid;

      //add a referring member/account if it exists
      if (this.referring_account != null) {
        data["referring_account__c"] = this.referring_account;
      }

      if (this.campaign_state_value) {
        data["campaignid"] = this.campaign_sfid;
      }

      // Create Opportunity
      postOpportunity(data).then((response) => {
        console.log("opportunity response: " + response);

        data = {};

        data["contactid"] = this.contact_sfid;
        data["opportunity__heroku_external_id__c"] = opp_guid;
        data["role"] = "Primary Member";

        // Create Contact Role
        postContactRole(data).then(async (response) => {
          console.log("contactroles response: " + response);

          // Insert Opportunity Line Items
          const selected_products = this.buildSelectedProducts();
          await this.createOLIPromises(selected_products, opp_guid, true); // isRenewal = true
          console.log("All opportunity line items inserted successfully");

          // Now insert payment record AFTER all OLIs are done
          // Step 1: Insert the appropriate payment record
          const isLocallyCollected =
            this.funds_collected_locally === true ||
            this.funds_collected_locally === "true";

          data = {};
          const dateObj = new Date();
          const month = dateObj.getUTCMonth() + 1;
          const day = dateObj.getUTCDate();
          const year = dateObj.getUTCFullYear();
          const newdate = `${month}/${day}/${year}`;

          // Only record a Credit Card payment when a real charge succeeded
          // (transId = Stripe PaymentIntent). Trials, $0, or funds collected
          // locally have no transId and fall through to the Credit branch,
          // preventing DI "hybrid" (Credit + card metadata) records.
          if (!isLocallyCollected && this.transId) {
            // Stripe payment record (charged via Payment Intent)
            console.log("Inserting Stripe payment record");
            data.pymt__processor_connection__c = STRIPE_PROCESSOR_CONNECTION_SFID;
            data.pymt__log__c = "Payment via Stripe";
            data.pymt__payment_processor__c = "Stripe";
            data.pymt__payment_type__c = "Credit Card";
            data.pymt__card_type__c = this.stripeCardBrand || "Unknown";
            data.pymt__last_4_digits__c = this.stripeCardLast4 || "";
            data.pymt__transaction_id__c = this.transId; // Payment Intent ID
            data.pymt__account__c = this.account_sfid;
            data.pymt__contact__c = this.contact_sfid;
            data.pymt__opportunity__r__heroku_external_id__c = opp_guid;
            data.pymt__status__c = "Completed";
            data.pymt__amount__c = this.paymentAmount;
            data.pymt__date__c = newdate;
            data.herokudirect__c = false;
            data.name = "Payment via Membership App (Stripe)";
            data.create_source__c = process.env.VUE_APP_CREATE_SOURCE;
            data.processed_by_username = this.cachedName;
            data.consent = this.consentObject;
          } else {
            // Credit payment record (funds collected locally)
            console.log(
              "Inserting Credit payment record (funds collected locally)"
            );
            // NOTE: the /payments API requires pymt__processor_connection__c and
            // pymt__payment_processor__c (reqparse required=True) — omitting them
            // makes the endpoint silently return 200 without inserting. Keep them.
            // The DI "hybrid" cleanup must be solved server-side, not by dropping these.
            data["pymt__processor_connection__c"] = STRIPE_PROCESSOR_CONNECTION_SFID;
            data["pymt__payment_type__c"] = "Credit";
            data["pymt__log__c"] = "Credit Payment";
            data["pymt__payment_processor__c"] = "Stripe";
            data["pymt__account__c"] = this.account_sfid;
            data["pymt__contact__c"] = this.contact_sfid;
            data["pymt__opportunity__r__heroku_external_id__c"] = opp_guid;
            data["pymt__status__c"] = "Completed";
            data["pymt__amount__c"] = this.paymentAmount;
            data["pymt__date__c"] = newdate;
            data["name"] = "Credit Payment via Membership App";
            data["create_source__c"] = process.env.VUE_APP_CREATE_SOURCE;
            data["heroku_external_id__c"] = this.guid();
            data["processed_by_username"] = this.cachedName;
            data["consent"] = this.consentObject;
          }

          console.log("Payment data:", data);

          try {
            const paymentResponse = await postPayment(this.attachActivationCode(data));
            console.log("Payment insertion successful:", paymentResponse);

            // Step 2: Sync Stripe Customer and Payment Method to Salesforce
            await this.syncStripeForRenewal(opp_guid);

            // Show success message
            this.trackUxcamSubmit("succeeded", { path: "stripe_renewal" });
            this.showModal();
            this.$bvToast.toast(
              "The member was renewed successfully. The form has been reset.",
              {
                variant: "success",
                title: "Member renewed successfully.",
                autoHideDelay: 5000,
              }
            );
          } catch (paymentError) {
            console.error("Failed to insert payment record:", paymentError);
            this.trackUxcamSubmit("failed", {
              path: "stripe_renewal",
              reason: "payment_record_insert",
              message: paymentError?.message,
            });
            this.$bvToast.toast(
              "Failed to insert payment record. Please try again or contact support.",
              {
                variant: "danger",
                title: "Payment Record Error",
                autoHideDelay: 10000,
              }
            );
          }
        });
      });

      this.updateExistingSfRecords({
        account_parsed_obj,
        account_keynames,
        contact_parsed_obj,
        contact_keynames,
        boat_parsed_obj,
        boat_keynames,
        membership_parsed_obj,
        membership_keynames,
      });

      this.$bvToast.toast("Update successful.", {
        title: "Membership updated",
        variant: "success",
        autoHideDelay: 5000,
      });

      // Send confirmation email
      data = {};
      data["to_list"] = this.getEmailList;
      data["body"] =
        this.account.name +
        " has been successfully renewed." +
        "\n\n" +
        this.getEmailBody;
      data["subject"] = "Member successfully renewed from Membership App";

      console.log("Sending renewal confirmation email");

      this.showModal();

      postEmail(data).then((response) => {
        console.log(response);
      });
    },
    handleNewMemberSubmission(opp_guid, {
      account_parsed_obj,
      account_keynames,
      contact_parsed_obj,
      contact_keynames,
      boat_parsed_obj,
      boat_keynames,
      membership_parsed_obj,
      membership_keynames,
    }) {
      // ===== NEW MEMBER FLOW =====
      let data = {};
      console.log("starting new submission");
      const acc_guid = this.guid();
      data["heroku_external_id__c"] = acc_guid;
      data["account_detail_type__c"] = "Customer - Retail";
      data["type"] = "General";
      data["name"] = this.account.name;
      data["recordtypeid"] = "01237000000Tgx2AAC";
      account_keynames.forEach((field) => {
        data[field] = account_parsed_obj[field];
      });

      // Create Account
      postAccount(data).then((response) => {
        data = {};

        if (!("error" in response)) {
          const cont_guid = this.guid();
          data["account__heroku_external_id__c"] = acc_guid;
          data["recordtypeid"] = RECORDTYPE_PERSON_CONTACT;
          data["heroku_external_id__c"] = cont_guid;
          contact_keynames.forEach((field) => {
            data[field] = contact_parsed_obj[field];
          });

          // Create Contact
          postContact(data).then((response) => {
            data = {};
            if (!("error" in response)) {
              const memb_guid = this.guid();
              data["account__r__heroku_external_id__c"] = acc_guid;
              data["membership_contact__r__heroku_external_id__c"] =
                cont_guid;
              data["heroku_external_id__c"] = memb_guid;

              membership_keynames.forEach((field) => {
                data[field] = membership_parsed_obj[field];
              });

              // Create Membership
              postMembership(data).then(async (response) => {
                console.log("Membership created successfully");

                // Insert Stripe Customer and Payment Method records
                const stripeOk = await this.setupNewMemberStripe(
                  acc_guid,
                  cont_guid,
                  memb_guid,
                  opp_guid
                );
                if (!stripeOk) {
                  return;
                }

                data = {};
                if (!("error" in response)) {
                  const boat_guid = this.guid();

                  data["heroku_external_id__c"] = boat_guid;
                  data["account__r__heroku_external_id__c"] = acc_guid;
                  data["contact__r__heroku_external_id__c"] = cont_guid;
                  data["related_membership__r__heroku_external_id__c"] =
                    memb_guid;

                  boat_keynames.forEach((field) => {
                    data[field] = boat_parsed_obj[field];
                  });

                  // Create Boat
                  postBoat(data).then((response) => {
                    data = {};
                    if (!("error" in response)) {
                      data["heroku_external_id__c"] = opp_guid;
                      data["name"] = "Pending Invoice Number";
                      data["closedate"] = new Date().toISOString();
                      data["stagename"] = "Invoice Open";
                      data["account__heroku_external_id__c"] = acc_guid;
                      data["membership__r__heroku_external_id__c"] =
                        memb_guid;

                      if (this.campaign_state_value) {
                        data["campaignid"] = this.campaign_sfid;
                      }

                      if (this.referring_account != null) {
                        data["referring_account__c"] = this.referring_account;
                      }

                      // Create Opportunity
                      postOpportunity(data).then((response) => {
                        if (!("error" in response)) {
                          console.log("opp insert");
                          console.log(response);
                          data = {};

                          data["contact__heroku_external_id__c"] = cont_guid;
                          data["opportunity__heroku_external_id__c"] =
                            opp_guid;
                          data["role"] = "Primary Member";

                          // Create Contact Role
                          postContactRole(data).then(async (response) => {
                            if (!("error" in response)) {
                              // Insert Opportunity Line Items
                              const selected_products =
                                this.buildSelectedProducts();
                              await this.createOLIPromises(
                                selected_products,
                                opp_guid,
                                false
                              ); // isRenewal = false
                              console.log(
                                "All opportunity line items inserted successfully (new member)"
                              );

                              // Now insert payment record AFTER all OLIs are done
                              data = {};
                              var dateObj = new Date();
                              var month = dateObj.getUTCMonth() + 1;
                              var day = dateObj.getUTCDate();
                              var year = dateObj.getUTCFullYear();
                              var newdate = month + "/" + day + "/" + year;

                              if (
                                (this.funds_collected_locally === false ||
                                  this.funds_collected_locally === "false") &&
                                this.transId
                              ) {
                                // Stripe-specific payment record
                                data["pymt__processor_connection__c"] =
                                  STRIPE_PROCESSOR_CONNECTION_SFID;
                                data["pymt__log__c"] = "Payment via Stripe";
                                data["pymt__payment_processor__c"] = "Stripe";
                                data["pymt__payment_type__c"] = "Credit Card";
                                data["pymt__card_type__c"] =
                                  this.stripeCardBrand || "Unknown";
                                data["pymt__last_4_digits__c"] =
                                  this.stripeCardLast4 || "";
                                data["pymt__transaction_id__c"] =
                                  this.transId;
                                // Note: pymt__authorization_id__c skipped for Stripe
                                data[
                                  "pymt__account__r__heroku_external_id__c"
                                ] = acc_guid;
                                data[
                                  "pymt__contact__r__heroku_external_id__c"
                                ] = cont_guid;
                                data[
                                  "pymt__opportunity__r__heroku_external_id__c"
                                ] = opp_guid;
                                data["pymt__status__c"] = "Completed";
                                data["pymt__amount__c"] = this.paymentAmount;
                                data["pymt__date__c"] = newdate;
                                data["herokudirect__c"] = false;
                                data["name"] =
                                  "Payment via Membership App (Stripe)";
                                data["create_source__c"] =
                                  process.env.VUE_APP_CREATE_SOURCE;
                                data["processed_by_username"] = this.cachedName;
                                data["consent"] = this.consentObject;

                                console.log("Stripe payment data:", data);

                                await postPayment(this.attachActivationCode(data));
                                console.log(
                                  "Stripe payment insertion successful"
                                );
                              } else {
                                // Funds collected locally, $0, or no successful
                                // charge (no transId) → record as Credit, never
                                // Credit Card, to avoid DI "hybrid" records.
                                console.log(
                                  "funds collected locally - insert credit payment"
                                );

                                // NOTE: /payments API requires
                                // pymt__processor_connection__c and
                                // pymt__payment_processor__c (reqparse
                                // required=True) — omitting them makes the
                                // endpoint silently 200 without inserting. Keep
                                // them; solve the DI "hybrid" server-side.
                                data["pymt__processor_connection__c"] =
                                  STRIPE_PROCESSOR_CONNECTION_SFID;
                                data["pymt__payment_type__c"] = "Credit";
                                data["pymt__log__c"] = "Credit Payment";
                                data["pymt__payment_processor__c"] = "Stripe";
                                data[
                                  "pymt__account__r__heroku_external_id__c"
                                ] = acc_guid;
                                data[
                                  "pymt__contact__r__heroku_external_id__c"
                                ] = cont_guid;
                                data[
                                  "pymt__opportunity__r__heroku_external_id__c"
                                ] = opp_guid;
                                data["pymt__status__c"] = "Completed";
                                data["pymt__amount__c"] = this.paymentAmount;
                                data["pymt__date__c"] = newdate;
                                data["name"] =
                                  "Credit Payment via Membership App";
                                data["create_source__c"] =
                                  process.env.VUE_APP_CREATE_SOURCE;
                                data["processed_by_username"] = this.cachedName;
                                data["consent"] = this.consentObject;

                                await postPayment(this.attachActivationCode(data));
                                console.log(
                                  "Credit payment insertion successful"
                                );
                              }

                              // Show success message and send email AFTER payment is complete
                              this.trackUxcamSubmit("succeeded", {
                                path: "stripe_new_member",
                              });
                              this.showModal();
                              this.$bvToast.toast(
                                "The member was inserted succesfully. The form has been reset.",
                                {
                                  variant: "success",
                                  title: "Member inserted successfully.",
                                  autoHideDelay: 5000,
                                }
                              );

                              // Send confirmation email
                              data = {};
                              data["to_list"] = this.getEmailList;
                              data["body"] =
                                this.account.name +
                                " has been successfully submitted into the database." +
                                "\n\n" +
                                this.getEmailBody;
                              data["subject"] =
                                "Membership successfully inserted from Membership App";

                              console.log("Sending new member email");

                              postEmail(data).then((response) => {
                                console.log(response);
                              });
                            }
                          }).catch((e) =>
                            this.handlePostChargeSfFailure(
                              "contact role / line items / payment",
                              e
                            )
                          );
                        }
                      }).catch((e) =>
                        this.handlePostChargeSfFailure("opportunity", e)
                      );
                    }
                  }).catch((e) =>
                    this.handlePostChargeSfFailure("boat", e)
                  );
                }
              }).catch((e) =>
                this.handlePostChargeSfFailure("membership", e)
              );
            }
          }).catch((e) =>
            this.handlePostChargeSfFailure("contact", e)
          );
        }
      }).catch((e) => this.handlePostChargeSfFailure("account", e));
    },
    // Sync the Stripe customer + payment method to Salesforce after a
    // renewal payment, cancelling or creating the subscription schedule to
    // match the auto-renew selection. Runs regardless of
    // funds_collected_locally, as long as Stripe IDs exist (e.g. when ARB
    // is enabled and the card was collected via Setup Intent).
    async syncStripeForRenewal(opp_guid) {
      if (!this.stripeCustomerId || !this.stripePaymentMethodId) {
        return;
      }

      const isUpdate = this.existingStripeCustomerSfid != null;
      console.log(
        isUpdate
          ? "Updating Stripe customer and payment method records (renewal)"
          : "Inserting Stripe customer and payment method records (renewal)"
      );
      console.log("DEBUG - account_sfid:", this.account_sfid);
      console.log("DEBUG - contact_sfid:", this.contact_sfid);
      console.log("DEBUG - stripeExpMonth:", this.stripeExpMonth);
      console.log("DEBUG - stripeExpYear:", this.stripeExpYear);
      console.log(
        "DEBUG - existingStripeCustomerSfid:",
        this.existingStripeCustomerSfid
      );

      // Cancel subscription schedules if auto-renew is disabled
      if (!this.arb_value && this.existingStripeCustomerId) {
        try {
          console.log(
            "Auto-renew disabled, cancelling subscription schedules..."
          );
          await manageSubscriptionSchedule({
            customer_id:
              typeof this.existingStripeCustomerId === "object"
                ? this.existingStripeCustomerId.id
                : this.existingStripeCustomerId,
            membership_sfid: this.membership_sfid,
            auto_renew: false,
          });
          console.log(
            "Subscription schedules cancelled successfully"
          );
        } catch (cancelError) {
          console.error(
            "Failed to cancel subscription schedules:",
            cancelError
          );
          console.error(
            "Cancel error details:",
            cancelError.response?.data
          );
          // Continue with submission - don't block on cancellation failure
        }
      }

      const stripeData = {
        account_sfid: this.account_sfid,
        contact_sfid: this.contact_sfid,
        stripe_customer_id:
          typeof this.stripeCustomerId === "object"
            ? this.stripeCustomerId.id
            : this.stripeCustomerId,
        stripe_payment_method_id: this.stripePaymentMethodId,
        card_type: this.stripeCardBrand || "Unknown",
        last_4_digits: this.stripeCardLast4 || "",
        expiration_month: this.stripeExpMonth,
        expiration_year: this.stripeExpYear,
        is_renewal: true,
        is_update: isUpdate,
        existing_customer_sfid: this.existingStripeCustomerSfid,
      };

      console.log(
        "DEBUG - Full stripeData object:",
        JSON.stringify(stripeData, null, 2)
      );

      try {
        const stripeResponse = await insertStripeCustomerPaymentMethod(stripeData);

        console.log(
          isUpdate
            ? "Stripe customer and payment method updated (renewal):"
            : "Stripe customer and payment method inserted (renewal):",
          stripeResponse
        );
        console.log(
          "DEBUG - stripeResponse.data:",
          stripeResponse.data
        );
        console.log(
          "DEBUG - stripeResponse.data.payment_method_sfid:",
          stripeResponse.data.payment_method_sfid
        );
        console.log(
          "DEBUG - stripeResponse.data.sfid:",
          stripeResponse.data.sfid
        );
        console.log(
          "DEBUG - stripeResponse.data.stripe_customer_sfid:",
          stripeResponse.data.stripe_customer_sfid
        );
        console.log(
          "DEBUG - stripeResponse.data.payment_method_heroku_id:",
          stripeResponse.data.payment_method_heroku_id
        );

        const hasCustomerId =
          stripeResponse.data?.sfid ||
          stripeResponse.data?.stripe_customer_sfid;
        const hasPaymentMethodId =
          stripeResponse.data?.payment_method_heroku_id;

        if (
          !stripeResponse.data ||
          (!hasCustomerId && !hasPaymentMethodId)
        ) {
          throw new Error(
            "Stripe customer/payment method operation succeeded but did not return required IDs"
          );
        }

        // Step 3: Create subscription schedule if auto-renew is enabled
        console.log("DEBUG - arb_value:", this.arb_value);
        console.log(
          "DEBUG - activation_codes:",
          this.activation_codes
        );
        console.log(
          "DEBUG - activation_codes?.name:",
          this.activation_codes?.name
        );

        if (this.arb_value) {
          console.log(
            "Creating subscription schedule for renewal..."
          );

          const scheduleResponse = await manageSubscriptionSchedule({
            account_sfid: this.account_sfid,
            contact_sfid: this.contact_sfid,
            opportunity_heroku_id: opp_guid,
            membership_sfid: this.membership_sfid,
            customer_id:
              typeof this.stripeCustomerId === "object"
                ? this.stripeCustomerId.id
                : this.stripeCustomerId,
            payment_method_id: this.stripePaymentMethodId,
            auto_renew: true,
            is_renewal: true,
            membership_type: this.CardSelection,
            trailer_care_type:
              this.TrailerSelection !== "None"
                ? this.TrailerSelection
                : null,
            promotion_code_sfid: this.promotion_sfid,
            promotion_type: this.promotion_ongoing_promotion_type,
            activation_code_used: !!(
              this.activation_codes && this.activation_codes.name
            ),
            current_expiration_date:
              this.membership_expiration_date__c,
            bonus_days: this.promotion_value_in_days || 0,
            payment_method_heroku_id:
              stripeResponse.data.payment_method_heroku_id,
            is_trial: this.CardSelection.includes("Trial"),
            trial_days: this.getTrialDays,
            trial_product_id: this.selected_trial_time_product,
          });

          console.log(
            "Subscription schedule created (renewal):",
            scheduleResponse.data
          );

          if (
            !scheduleResponse.data ||
            !scheduleResponse.data.subscription_schedule_id
          ) {
            console.warn(
              "Subscription schedule created but response missing subscription_schedule_id"
            );
          }
        }
      } catch (stripeError) {
        console.error(
          "CRITICAL ERROR - Failed to sync Stripe data to Salesforce (renewal):",
          stripeError
        );
        console.error(
          "Error response data:",
          stripeError.response?.data
        );

        this.$bvToast.toast(
          "Failed to set up Stripe auto-renewal in Salesforce. Payment was processed but auto-renewal may not be configured. Please contact support.",
          {
            variant: "danger",
            title: "Error Setting Up Auto-Renewal",
            autoHideDelay: 10000,
          }
        );

        console.error(
          "MANUAL INTERVENTION REQUIRED: Renewal payment processed but Stripe customer/subscription not synced to Salesforce"
        );
      }
    },
    // Insert the Stripe customer / payment-method records (with retry) and
    // create the subscription schedule for a new member. Returns false when
    // the sync definitively failed and the submission must stop.
    async setupNewMemberStripe(acc_guid, cont_guid, memb_guid, opp_guid) {
      if (!this.stripeCustomerId || !this.stripePaymentMethodId) {
        return true;
      }

      console.log(
        "Inserting Stripe customer and payment method records (new member)"
      );
      console.log("DEBUG - acc_guid:", acc_guid);
      console.log("DEBUG - cont_guid:", cont_guid);
      console.log("DEBUG - stripeExpMonth:", this.stripeExpMonth);
      console.log("DEBUG - stripeExpYear:", this.stripeExpYear);

      const stripeData = {
        account_heroku_id: acc_guid,
        contact_heroku_id: cont_guid,
        stripe_customer_id:
          typeof this.stripeCustomerId === "object"
            ? this.stripeCustomerId.id
            : this.stripeCustomerId,
        stripe_payment_method_id: this.stripePaymentMethodId,
        card_type: this.stripeCardBrand || "Unknown",
        last_4_digits: this.stripeCardLast4 || "",
        expiration_month: this.stripeExpMonth,
        expiration_year: this.stripeExpYear,
        is_renewal: false, // Flag to indicate this is a new member
      };

      console.log(
        "DEBUG - Full stripeData object (new member):",
        JSON.stringify(stripeData, null, 2)
      );

      try {
        const stripeResponse = await this.insertStripeWithRetry(stripeData);

        console.log(
          "Stripe customer and payment method inserted (new member):",
          stripeResponse
        );
        console.log(
          "DEBUG - stripeResponse.data:",
          stripeResponse.data
        );
        console.log(
          "DEBUG - stripeResponse.data.payment_method_sfid:",
          stripeResponse.data.payment_method_sfid
        );
        console.log(
          "DEBUG - stripeResponse.data.sfid:",
          stripeResponse.data.sfid
        );

        const hasCustomerId =
          stripeResponse.data?.sfid ||
          stripeResponse.data?.stripe_customer_sfid;
        const hasPaymentMethodId =
          stripeResponse.data?.payment_method_heroku_id;

        if (
          !stripeResponse.data ||
          (!hasCustomerId && !hasPaymentMethodId)
        ) {
          throw new Error(
            "Stripe customer/payment method operation succeeded but did not return required IDs"
          );
        }

        // Create subscription schedule if auto-renew is enabled
        if (this.arb_value) {
          console.log(
            "DEBUG - Inside subscription schedule creation conditional (new member)"
          );
          console.log("DEBUG - memb_guid value:", memb_guid);
          console.log("DEBUG - acc_guid value:", acc_guid);
          console.log("DEBUG - cont_guid value:", cont_guid);
          console.log("DEBUG - opp_guid value:", opp_guid);
          try {
            console.log(
              "Creating subscription schedule for new member..."
            );
            const scheduleResponse = await manageSubscriptionSchedule({
              account_heroku_id: acc_guid,
              contact_heroku_id: cont_guid,
              opportunity_heroku_id: opp_guid,
              membership_heroku_id: memb_guid,
              customer_id:
                typeof this.stripeCustomerId === "object"
                  ? this.stripeCustomerId.id
                  : this.stripeCustomerId,
              payment_method_id: this.stripePaymentMethodId,
              auto_renew: true,
              is_renewal: false,
              membership_type: this.CardSelection,
              trailer_care_type:
                this.TrailerSelection !== "None"
                  ? this.TrailerSelection
                  : null,
              promotion_code_sfid: this.promotion_sfid,
              promotion_type:
                this.promotion_ongoing_promotion_type,
              activation_code_used: !!(
                this.activation_codes &&
                this.activation_codes.name
              ),
              bonus_days: this.promotion_value_in_days || 0,
              payment_method_heroku_id:
                stripeResponse.data.payment_method_heroku_id,
              is_trial: this.CardSelection.includes("Trial"),
              trial_days: this.getTrialDays,
              trial_product_id:
                this.selected_trial_time_product,
            });
            console.log(
              "Subscription schedule created (new member):",
              scheduleResponse.data
            );
          } catch (scheduleError) {
            console.error(
              "Failed to create subscription schedule (new member):",
              scheduleError
            );
            console.error(
              "Schedule error details:",
              scheduleError.response?.data
            );
            // Continue with submission - don't block on subscription failure
          }
        }
      } catch (stripeError) {
        console.error(
          "Failed to insert Stripe customer/payment method (new member):",
          stripeError
        );
        console.error(
          "Error response data:",
          stripeError.response?.data
        );
        this.trackUxcamSubmit("failed", {
          path: "stripe_new_member",
          reason: "salesforce_sync",
          message: stripeError?.message,
        });
        this.$bvToast.toast(
          "Failed to sync payment records to Salesforce after multiple attempts. The payment was processed successfully in Stripe. Please contact the helpdesk with the member's details.",
          {
            title: "Error syncing payment records",
            variant: "danger",
            autoHideDelay: 10000,
          }
        );
        this.disableSubmit = false;
        return false;
      }

      return true;
    },
    async insertStripeWithRetry(stripeData) {
      // Retry insert-stripe-cus-pm up to 3 times to handle transient failures
      let stripeResponse;
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          stripeResponse = await insertStripeCustomerPaymentMethod(stripeData);
          break; // Success, exit retry loop
        } catch (retryError) {
          console.error(
            `insert-stripe-cus-pm attempt ${attempt}/${maxRetries} failed:`,
            retryError.response?.data || retryError.message
          );
          if (attempt === maxRetries) throw retryError;
          // Wait before retrying (1s, then 2s)
          await new Promise((resolve) =>
            setTimeout(resolve, attempt * 1000)
          );
        }
      }

      return stripeResponse;
    },
    updateExistingSfRecords({
      account_parsed_obj,
      account_keynames,
      contact_parsed_obj,
      contact_keynames,
      boat_parsed_obj,
      boat_keynames,
      membership_parsed_obj,
      membership_keynames,
    }) {
      var sfid_parsed_obj = JSON.parse(JSON.stringify(this.routes));
      var sfid_keynames = Object.keys(sfid_parsed_obj);

      // Update existing Salesforce records
      let data = {};
      sfid_keynames
        .filter((v) => (v.endsWith("s") && v != "arbs") || v == "account")
        .forEach((field) => {
          if (field == "account") {
            field = "accounts";
            account_keynames.forEach((field) => {
              data[field] = account_parsed_obj[field];
            });
            data["sfid"] = this.account_sfid;
          } else if (field == "boats") {
            boat_keynames.forEach((field) => {
              data[field] = boat_parsed_obj[field];
            });
            data["sfid"] = this.boat_sfid;
          } else if (field == "memberships") {
            membership_keynames.forEach((field) => {
              data[field] = membership_parsed_obj[field];
            });
            data["sfid"] = this.membership_sfid;
          } else if (field == "contacts") {
            contact_keynames.forEach((field) => {
              data[field] = contact_parsed_obj[field];
            });
            data["sfid"] = this.contact_sfid;
          }

          const patchFn = {
            accounts: patchAccount,
            boats: patchBoat,
            memberships: patchMembership,
            contacts: patchContact,
          }[field];
          patchFn(data)
            .then((response) => {
              console.log(response);
            })
            .catch(function (error) {
              console.error("Error updating Salesforce record:", error);
            });

          data = {};
        });
    },
  },
};
